import React, { useCallback, useEffect, useRef, useState } from "react";
import ExportCSVButton from "../../../shared/ExportCSVButton";
import Loader from "../../../common/Loader";
import CrossDataTable from "./CrossDataTable";
import YearOverYearComparisonIntervalChart from "../../../shared/YearOverYearComparisonIntervalChart";
import TransportationMetrics from "./TransportationMetrics";

import TripEventsApi from "../../../../services/resources/TripEventsApi";
import { ApiCallErrorMessageHandler } from "../../../../lib/coc-common-scripts";
import { formatNumber, pluralizeText } from "../../../../lib";
import axios from "axios";
import moment from "moment";

// venueId sentinels used only for column ordering: the summed "Total" column sorts first,
// the "unassigned" bucket sorts last, and real venues sort by their id in between.
const totalVenueId = -1;
const unassignedVenueSortId = Number.MAX_SAFE_INTEGER;

const venueSortId = (venueId: number | null) =>
  venueId == null ? unassignedVenueSortId : venueId;

const calcPercentage = (value: number, total: number) =>
  total > 0 ? Math.round((value * 100) / total) : 0;

type NumericColumn = {
  venueId: number;
  label: string;
  total: number;
  data: number[];
};

// builds the columns for a numeric cross-tab section: one per venue plus a summed "Total".
// getData maps a venue's metrics to its bar values (data[0] is treated as the column total).
// the unassigned bucket is only shown when showUnassigned is true; when there are no real
// venues only the Total column is shown (e.g. a trip with no venues defined).
function buildNumericCrossData<T>(
  rows: VenueMetrics<T>[],
  getData: (metrics: T) => number[],
  showUnassigned: boolean,
  hasRealVenues: boolean,
): NumericColumn[] {
  const venueColumns = (rows || [])
    .filter((r) => r.venueId != null || showUnassigned)
    .map((r) => {
      const data = getData(r.metrics);
      return {
        venueId: venueSortId(r.venueId),
        label: r.venueName,
        total: data[0],
        data,
      };
    });

  // the Total is summed across every venue (including unassigned), regardless of what is displayed
  const totalData = (rows || [])
    .map((r) => getData(r.metrics))
    .reduce(
      (acc: number[], data) => data.map((value, i) => (acc[i] || 0) + value),
      [] as number[],
    );
  const totalColumn: NumericColumn = {
    venueId: totalVenueId,
    label: "Total",
    total: totalData[0] || 0,
    data: totalData,
  };

  return hasRealVenues ? [...venueColumns, totalColumn] : [totalColumn];
}

interface TripMetricsProps {
  tripEvent: {
    id: string;
    programScheduleName: string;
    daysToStart: number;
    isTravelTrip: boolean;
  };
  systemLists: any;
}

export default function TripMetrics(props: TripMetricsProps) {
  const {
    tripEvent: {
      id: tripEventId,
      programScheduleName,
      daysToStart,
      isTravelTrip,
    },
    systemLists,
  } = props;

  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [metrics, setMetrics] = useState({} as MetricsApi);
  const [success, setSuccess] = useState(true);

  const apiSignalRef = useRef(axios.CancelToken.source());
  useEffect(() => {
    const _apiSignal = apiSignalRef.current;
    return () => {
      _apiSignal.cancel();
    };
  }, []);

  const getMetrics = useCallback(() => {
    setErrorMessage("");
    setLoading(true);
    setSuccess(true);

    TripEventsApi.getTripMetrics(apiSignalRef.current.token, tripEventId)
      .then((metrics) => {
        setLoading(false);
        setMetrics(metrics);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          setErrorMessage(ApiCallErrorMessageHandler(err));
          setLoading(false);
          setSuccess(false);
        }
      });
  }, [tripEventId]);

  useEffect(() => {
    getMetrics();
  }, [getMetrics]);

  const {
    checkedInAttendees,
    event,
    studentClasses,
    tracks,
    tourSchedules,
    venuePreferences,
    enrollmentReach,
  } = metrics;

  const eventVenues = event || [];
  const realVenues = eventVenues.filter((v) => v.venueId != null);
  const hasRealVenues = realVenues.length > 0;

  // the unassigned bucket is only shown as its own column when it actually has attendees;
  // its counts are always still included in the Total column
  const unassignedEvent = eventVenues.find((v) => v.venueId == null);
  const showUnassigned =
    !!unassignedEvent &&
    unassignedEvent.metrics.allStudentCount +
      unassignedEvent.metrics.allChaperonsCount +
      unassignedEvent.metrics.otherAttendeesCount >
      0;

  const hasMultipleVenues = hasRealVenues;

  return (
    <div className="trip-metrics">
      {loading ? (
        <div>
          <Loader />
        </div>
      ) : !success ? (
        <div className="text-center">
          <img src="/img/error.svg" alt="error robot" height="240" />
          <p className="xxl-text mt-24 mb-16">An error has occured</p>
          <p className="medium-text accent-text fw-500">
            {errorMessage || "Our server is being stubborn, please try again"}
          </p>
        </div>
      ) : (
        Object.keys(metrics).length > 0 && (
          <>
            <Header
              eventMetrics={eventVenues}
              showUnassigned={showUnassigned}
              hasRealVenues={hasRealVenues}
            />

            <div className="divider" />

            {!isTravelTrip && (
              <AttendeesMetrics
                eventMetrics={eventVenues}
                showUnassigned={showUnassigned}
                hasRealVenues={hasRealVenues}
              />
            )}

            <StudentsMetrics
              studentMetrics={eventVenues}
              tracks={tracks}
              studentClasses={studentClasses}
              showUnassigned={showUnassigned}
              hasRealVenues={hasRealVenues}
            />

            {!isTravelTrip && (
              <CheckedInAttendees
                checkedInAttendees={checkedInAttendees}
                showUnassigned={showUnassigned}
                hasRealVenues={hasRealVenues}
              />
            )}

            <ChabadHousesAndSchools
              enrollmentReach={enrollmentReach}
              showUnassigned={showUnassigned}
              hasRealVenues={hasRealVenues}
            />

            <div className="divider" />

            {hasMultipleVenues && (
              <VenuePreferences venuePreferences={venuePreferences} />
            )}

            <TourSchedules
              tourSchedules={tourSchedules}
              tripEventId={tripEventId}
              programScheduleName={programScheduleName}
              apiSignal={apiSignalRef.current}
            />

            <YearOverYearComparison
              tripEventId={tripEventId}
              programScheduleName={programScheduleName}
              daysToStart={daysToStart}
              yearOverYearChartIntervals={
                systemLists.yearOverYearChartIntervals
              }
              apiSignal={apiSignalRef.current}
            />
          </>
        )
      )}
    </div>
  );
}

const Header = ({
  eventMetrics,
  showUnassigned,
  hasRealVenues,
}: {
  eventMetrics: VenueMetrics<EventMetrics>[];
  showUnassigned: boolean;
  hasRealVenues: boolean;
}) => {
  // mirror the column set and ordering the sections use so the header lines up
  const columns = hasRealVenues
    ? [
        ...eventMetrics
          .filter((v) => v.venueId != null || showUnassigned)
          .map((v) => ({ sortId: venueSortId(v.venueId), name: v.venueName })),
        { sortId: totalVenueId, name: "Total" },
      ]
    : [{ sortId: totalVenueId, name: "Total" }];

  columns.sort((a, b) => a.sortId - b.sortId);

  return (
    <div>
      <table className="metrics-table">
        <thead>
          <tr>
            {/* no title above the title column */}
            <th />

            {columns.map((column, i) => {
              return (
                <th
                  key={i}
                  className="large-text"
                  style={{ paddingLeft: "8px" }}
                >
                  {column.name}
                </th>
              );
            })}
          </tr>
        </thead>
      </table>
    </div>
  );
};

const AttendeesMetrics = ({
  eventMetrics,
  showUnassigned,
  hasRealVenues,
}: {
  eventMetrics: VenueMetrics<EventMetrics>[];
  showUnassigned: boolean;
  hasRealVenues: boolean;
}) => {
  const columnData = [
    { label: <p className="medium-text fw-700">Total attendees</p> },
    {
      label: (
        <div>
          <p className="medium-text fw-700 mb-0">Students</p>
          {/* TODO add number of applicants:  https://bitbean.atlassian.net/browse/COC-4750, for only travel trips or for both */}
          {/* <p className="small-text">num applicants here</p> */}
        </div>
      ),
      color: "#53B7E8",
    },
    {
      label: (
        <div>
          <p className="medium-text fw-700 mb-0">Shluchim & Chaperones</p>
          <p className="small-text">with accepted students</p>
        </div>
      ),
    },
    { label: <p className="medium-text">Shluchim</p>, color: "#0B425D" },
    { label: <p className="medium-text">Shluchos</p>, color: "#53B7E8" },
    { label: <p className="medium-text">Chaperones</p>, color: "#007AB5" },
    { label: <p className="medium-text fw-700">Other</p>, color: "#F3893D" },
  ];

  const crossData = buildNumericCrossData(
    eventMetrics,
    (m) => [
      m.allStudentCount + m.allChaperonsCount + m.otherAttendeesCount,
      m.allStudentCount,
      m.allChaperonsCount,
      m.chaperonShluchimCount,
      m.chaperonShluchosCount,
      m.chaperoneCount,
      m.otherAttendeesCount,
    ],
    showUnassigned,
    hasRealVenues,
  );

  const maxValue =
    crossData.find((d) => d.venueId === totalVenueId)?.total || 0;

  return (
    <CrossDataTable
      sectionLabel="Total attendees"
      maxValue={maxValue}
      columnData={columnData}
      crossData={crossData}
      isCrossTab
    />
  );
};

const StudentsMetrics = ({
  studentMetrics,
  studentClasses,
  tracks,
  showUnassigned,
  hasRealVenues,
}: {
  studentMetrics: VenueMetrics<EventMetrics>[];
  studentClasses: VenueMetrics<StudentClassAggregate[]>[];
  tracks: VenueMetrics<TrackAggregate[]>[];
  showUnassigned: boolean;
  hasRealVenues: boolean;
}) => {
  const columnData = [
    { label: <p className="medium-text">Students by gender</p> },
    { label: <p className="medium-text">Students by class</p> },
    { label: <p className="medium-text">Students by track</p> },
  ];

  // the class/track sections are aligned to the event venues by venueId
  const classesByVenue = new Map<number | null, StudentClassAggregate[]>();
  (studentClasses || []).forEach((s) =>
    classesByVenue.set(s.venueId, s.metrics),
  );
  const tracksByVenue = new Map<number | null, TrackAggregate[]>();
  (tracks || []).forEach((t) => tracksByVenue.set(t.venueId, t.metrics));

  const genderPie = (m: EventMetrics) => [
    {
      name: "Male",
      value: m.maleStudentCount,
      percentageValue: m.maleStudentPercentage,
    },
    {
      name: "Female",
      value: m.femaleStudentCount,
      percentageValue: m.femaleStudentPercentage,
    },
  ];

  const classPie = (aggregates: StudentClassAggregate[]) =>
    (aggregates || []).map((a) => ({
      name: a.classDescription,
      value: a.classCount,
      percentageValue: a.studentPercentage,
    }));

  const trackPie = (aggregates: TrackAggregate[]) =>
    (aggregates || []).map((a) => ({
      name: a.trackName,
      value: a.trackRegistrationCount,
      percentageValue: a.trackRegistrationPercentage,
    }));

  const venueColumns = (studentMetrics || [])
    .filter((v) => v.venueId != null || showUnassigned)
    .map((v) => ({
      venueId: venueSortId(v.venueId),
      label: v.venueName,
      total: v.metrics.maleStudentCount + v.metrics.femaleStudentCount,
      data: [
        genderPie(v.metrics),
        classPie(classesByVenue.get(v.venueId) || []),
        trackPie(tracksByVenue.get(v.venueId) || []),
      ],
    }));

  // Total column: aggregate gender, class, and track distributions across all venues
  const totalMale = (studentMetrics || []).reduce(
    (sum, v) => sum + v.metrics.maleStudentCount,
    0,
  );
  const totalFemale = (studentMetrics || []).reduce(
    (sum, v) => sum + v.metrics.femaleStudentCount,
    0,
  );
  const totalStudents = totalMale + totalFemale;

  const classTotals = new Map<string, number>();
  (studentClasses || []).forEach((s) =>
    s.metrics.forEach((a) =>
      classTotals.set(
        a.classDescription,
        (classTotals.get(a.classDescription) || 0) + a.classCount,
      ),
    ),
  );
  const totalClassStudents = Array.from(classTotals.values()).reduce(
    (a, b) => a + b,
    0,
  );

  const trackTotals = new Map<number, { name: string; value: number }>();
  (tracks || []).forEach((t) =>
    t.metrics.forEach((a) => {
      const current = trackTotals.get(a.trackId) || {
        name: a.trackName,
        value: 0,
      };
      current.value += a.trackRegistrationCount;
      trackTotals.set(a.trackId, current);
    }),
  );
  const totalTrackRegistrations = Array.from(trackTotals.values()).reduce(
    (sum, t) => sum + t.value,
    0,
  );

  const totalColumn = {
    venueId: totalVenueId,
    label: "Total",
    total: totalStudents,
    data: [
      [
        {
          name: "Male",
          value: totalMale,
          percentageValue: calcPercentage(totalMale, totalStudents),
        },
        {
          name: "Female",
          value: totalFemale,
          percentageValue: calcPercentage(totalFemale, totalStudents),
        },
      ],
      Array.from(classTotals.entries()).map(([name, value]) => ({
        name,
        value,
        percentageValue: calcPercentage(value, totalClassStudents),
      })),
      Array.from(trackTotals.values()).map((t) => ({
        name: t.name,
        value: t.value,
        percentageValue: calcPercentage(t.value, totalTrackRegistrations),
      })),
    ],
  };

  const data = hasRealVenues ? [...venueColumns, totalColumn] : [totalColumn];

  return (
    <CrossDataTable
      sectionLabel="Students"
      crossData={data}
      isCrossTab={false}
      columnData={columnData}
    />
  );
};

const CheckedInAttendees = ({
  checkedInAttendees,
  showUnassigned,
  hasRealVenues,
}: {
  checkedInAttendees: VenueMetrics<CheckedInAttendees>[];
  showUnassigned: boolean;
  hasRealVenues: boolean;
}) => {
  const columnData = [
    { label: <p className="medium-text fw-700">Total</p> },
    { label: <p className="medium-text">Students</p>, color: "#0B425D" },
    {
      label: <p className="medium-text">Shluchim & Chaperones</p>,
      color: "#53B7E8",
    },
    { label: <p className="medium-text">Other</p>, color: "#F3893D" },
  ];

  const crossData = buildNumericCrossData(
    checkedInAttendees,
    (m) => [
      m.studentCheckedInCount +
        m.enrollmentAttendeeCheckedInCount +
        m.otherAttendeeCheckedInCount,
      m.studentCheckedInCount,
      m.enrollmentAttendeeCheckedInCount,
      m.otherAttendeeCheckedInCount,
    ],
    showUnassigned,
    hasRealVenues,
  );

  const maxValue =
    crossData.find((d) => d.venueId === totalVenueId)?.total || 0;

  return (
    <CrossDataTable
      sectionLabel="Attendees checked in"
      maxValue={maxValue}
      columnData={columnData}
      crossData={crossData}
      isCrossTab
    />
  );
};

const ChabadHousesAndSchools = ({
  enrollmentReach,
  showUnassigned,
  hasRealVenues,
}: {
  enrollmentReach: VenueMetrics<EnrollmentReach>[];
  showUnassigned: boolean;
  hasRealVenues: boolean;
}) => {
  const crossData = buildNumericCrossData(
    enrollmentReach,
    (m) => [m.enrollmentsWithStudentsCount, m.campusesWithStudentsCount],
    showUnassigned,
    hasRealVenues,
  );

  const totalChabadHousesCount = (enrollmentReach || []).reduce(
    (sum, v) => sum + v.metrics.chabadHouseCount,
    0,
  );
  const totalCampusesCount = (enrollmentReach || []).reduce(
    (sum, v) => sum + v.metrics.campusCount,
    0,
  );

  const columnData = [
    {
      label: (
        <div>
          <p className="medium-text fw-700 mb-0">
            Chabad Houses with accepted students
          </p>
          <p className="small-text">
            {totalChabadHousesCount} Chabad Houses enrolled
          </p>
        </div>
      ),
      color: "#53B7E8",
    },
    {
      label: (
        <div>
          <p className="medium-text fw-700 mb-0">
            Schools with accepted students
          </p>
          <p className="small-text">{totalCampusesCount} schools enrolled</p>
        </div>
      ),
      color: "#53B7E8",
    },
  ];

  return (
    <CrossDataTable
      sectionLabel="Chabad Houses & Schools"
      maxValue={Math.max(totalChabadHousesCount, totalCampusesCount)} // yes, this is a bit random
      columnData={columnData}
      crossData={crossData}
      isCrossTab
      hideClosedHeaders // it doesn't make sense to show totals on this section when closed
    />
  );
};

const VenuePreferences = ({
  venuePreferences,
}: {
  venuePreferences: VenueMetrics<VenuePreferences>[];
}) => {
  const columnData = [
    { label: <p className="medium-text fw-700">Total</p> },
    { label: <p className="medium-text">Students</p>, color: "#0B425D" },
    {
      label: <p className="medium-text">Shluchim & Shluchos</p>,
      color: "#53B7E8",
    },
    {
      label: <p className="medium-text">Chaperones</p>,
      color: "#007AB5",
    },
  ];

  const crossData = (venuePreferences || []).map((venue) => {
    const m = venue.metrics;
    const total =
      m.studentCount + m.shluchimAndShluchosCount + m.chaperoneCount;
    return {
      venueId: venueSortId(venue.venueId),
      label: venue.venueName,
      total,
      data: [
        total,
        m.studentCount,
        m.shluchimAndShluchosCount,
        m.chaperoneCount,
      ],
    };
  });

  // here the max is the sum of all totals
  const maxValue = crossData.reduce((acc, d) => acc + d.total, 0);

  return (
    <div>
      <p className="fw-700 large-text">Venue Preferences</p>

      <CrossDataTable
        sectionLabel="Venue preferences"
        maxValue={maxValue}
        columnData={columnData}
        crossData={crossData}
        isCrossTab
      />
    </div>
  );
};

const YearOverYearComparison = ({
  tripEventId,
  programScheduleName,
  daysToStart,
  yearOverYearChartIntervals,
  apiSignal,
}) => {
  const [registrationsYearOverYear, setRegistrationsYearOverYear] = useState(
    [],
  );
  const [
    includeAllEventsInComparisonChart,
    setIncludeAllEventsInComparisonChart,
  ] = useState(false);
  const [yearOverYearChartErrorMessage, setYearOverYearChartErrorMessage] =
    useState(null);
  const [yearOverYearChartLoading, setYearOverYearChartLoading] =
    useState(false);

  const getRegistrationsOverTimeComparison = useCallback(
    (includeAllEvents = includeAllEventsInComparisonChart) => {
      setYearOverYearChartErrorMessage(null);
      setYearOverYearChartLoading(true);
      setIncludeAllEventsInComparisonChart(includeAllEvents);

      TripEventsApi.getTripRegistrationsOverTimeComparison(
        apiSignal.token,
        tripEventId,
        includeAllEvents,
      )
        .then((registrationsYearOverYear) => {
          setYearOverYearChartLoading(false);
          setRegistrationsYearOverYear(registrationsYearOverYear);
        })
        .catch((err) => {
          if (!axios.isCancel(err)) {
            setYearOverYearChartErrorMessage(ApiCallErrorMessageHandler(err));
            setYearOverYearChartLoading(false);
          }
        });
    },
    [tripEventId, apiSignal, includeAllEventsInComparisonChart],
  );

  useEffect(
    () => {
      getRegistrationsOverTimeComparison();
    },
    // only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <YearOverYearComparisonIntervalChart
      useCurrency={false}
      chartTitle="Registrations Over Time Year to Year Comparison"
      endDateDescription="Trip Start"
      eventType="trip"
      eventTitle={programScheduleName}
      data={registrationsYearOverYear}
      daysRemaining={daysToStart}
      errorMessage={yearOverYearChartErrorMessage}
      getData={getRegistrationsOverTimeComparison}
      loading={yearOverYearChartLoading}
      includeAllEvents={includeAllEventsInComparisonChart}
      intervals={yearOverYearChartIntervals}
    />
  );
};

const TourSchedules = ({
  tourSchedules,
  tripEventId,
  programScheduleName,
  apiSignal,
}) => {
  if (!tourSchedules || !tourSchedules.length) {
    return null;
  }

  return (
    <>
      <div className="flex flex-justify-space flex-align-center mb-16">
        <p className="fw-700 large-text">Tour Schedules</p>
        <ExportTourSchedulesButton
          tripEventId={tripEventId}
          programScheduleName={programScheduleName}
          apiSignal={apiSignal}
        />
      </div>
      <div className="tour-metrics">
        {tourSchedules.map((tourSchedule) => {
          const {
            allAttendingCount,
            chaperoneCount,
            maximumParticipants,
            studentCount,
            tourName,
            tourScheduleName,
            tripTourScheduleID,
            busSize,
            groupSize,
            studentTransToCount,
            chapTransToCount,
            studentTransFromCount,
            chapTransFromCount,
            studentTransReturnCHCount,
            chapTransReturnCHCount,
            studentTransReturnJFKCount,
            chapTransReturnJFKCount,
            studentTransReturnLGACount,
            chapTransReturnLGACount,
            isOhelVisit,
            time,
          } = tourSchedule;

          const attendees = studentCount + chaperoneCount;
          const groupCount = Math.ceil(attendees / groupSize);

          return (
            <div key={tripTourScheduleID}>
              <div>
                <div className="flex flex-justify-space flex-align-center">
                  <div>
                    <p className="large-text fw-700 mb-0">
                      {tourName} - {tourScheduleName}
                    </p>
                    {time && (
                      <p className="accent-text small-text mb-8">
                        {moment(time).format("ddd, MMM D")} &middot;{" "}
                        {moment(time).format("h:mm A")}
                      </p>
                    )}
                  </div>
                  <p
                    className={
                      allAttendingCount > maximumParticipants
                        ? "error-text"
                        : ""
                    }
                  >
                    {maximumParticipants > 0 &&
                      `Max ${formatNumber(maximumParticipants)}`}
                  </p>
                </div>
                {studentCount > 0 ? (
                  <>
                    <p className="accent-text-dark mb-0">
                      {formatNumber(studentCount)}{" "}
                      {pluralizeText("Student", studentCount)} +{" "}
                      {formatNumber(chaperoneCount)}{" "}
                      {pluralizeText("Chaperone", chaperoneCount)}
                      /Shluchim ={" "}
                      <span className="fw-700">{attendees} Attendees</span>
                    </p>
                    {groupSize != null ? (
                      <p className="accent-text-dark mb-0">
                        {formatNumber(attendees)} Attendees ÷ {groupSize} group
                        slots ={" "}
                        <span className="fw-700">
                          {groupCount} {pluralizeText("Group", groupCount)}
                        </span>
                      </p>
                    ) : (
                      <p className="accent-text-dark italic-text mb-0">
                        Group size not set.
                      </p>
                    )}
                  </>
                ) : (
                  <p className="accent-text-dark italic-text mb-0">
                    There are no students registered.
                  </p>
                )}
              </div>
              {studentCount > 0 && (
                <div>
                  <TransportationMetrics
                    titleText="To"
                    studentCount={studentTransToCount}
                    chapsCount={chapTransToCount}
                    busSize={busSize}
                  />

                  {isOhelVisit ? (
                    <div>
                      <TransportationMetrics
                        titleText="From Ohel —> CH"
                        studentCount={studentTransReturnCHCount}
                        chapsCount={chapTransReturnCHCount}
                        busSize={busSize}
                      />
                      <TransportationMetrics
                        titleText="From Ohel —> JFK"
                        studentCount={studentTransReturnJFKCount}
                        chapsCount={chapTransReturnJFKCount}
                        busSize={busSize}
                      />
                      <TransportationMetrics
                        titleText="From Ohel —> LGA"
                        studentCount={studentTransReturnLGACount}
                        chapsCount={chapTransReturnLGACount}
                        busSize={busSize}
                      />
                    </div>
                  ) : (
                    <TransportationMetrics
                      titleText="From"
                      studentCount={studentTransFromCount}
                      chapsCount={chapTransFromCount}
                      busSize={busSize}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

const ExportTourSchedulesButton = ({
  tripEventId,
  programScheduleName,
  apiSignal,
}) => {
  const getTripExport = async (exportType) => {
    return await TripEventsApi.getTripReport(
      apiSignal.token,
      tripEventId,
      exportType,
    );
  };

  const getTourSchedulesExportHeaders = () => {
    const getTransportationCounts = (studentCount, chaperoneCount, busSize) => {
      const totalAttendees = studentCount + chaperoneCount;
      const busCount = Math.ceil((studentCount + chaperoneCount) / busSize);
      return `${totalAttendees} (${
        busSize
          ? `${busCount} ${pluralizeText("bus", busCount, "busses")}`
          : "Bus Size not set"
      })`;
    };
    return [
      { label: "Tour Name", value: "tourName" },
      {
        label: "Tour Schedule Name",
        value: "tourScheduleName",
      },
      { label: "Price", value: "price" },
      {
        label: "Included in Trip Price",
        value: ({ isPriceIncludedInTripPrice }) =>
          isPriceIncludedInTripPrice ? "Y" : "N",
      },
      {
        label: "Date & Time",
        value: ({ time }) => moment(time).format("MM/DD/YYYY hh:mm A"),
      },
      { label: "Duration Hours", value: "durationHours" },
      { label: "Duration Minutes", value: "durationMinutes" },
      {
        label: "Maximum Participants",
        value: "maximumParticipants",
      },
      {
        label: "Total Attendees Count",
        value: "allAttendingCount",
      },
      {
        label: "Chaperones/Shluchim Count",
        value: "chaperoneCount",
      },
      { label: "Student Count", value: "studentCount" },
      {
        label: "Total Groups",
        value: ({ groupSize, studentCount, chaperoneCount }) =>
          groupSize
            ? Math.ceil((studentCount + chaperoneCount) / groupSize)
            : "Group size not set",
      },
      {
        label: "Transportation To Count",
        value: ({ studentTransToCount, chapTransToCount, busSize }) =>
          getTransportationCounts(
            studentTransToCount,
            chapTransToCount,
            busSize,
          ),
      },
      {
        label: "Transportation From Count",
        value: ({
          studentTransFromCount,
          chapTransFromCount,
          busSize,
          isOhelVisit,
        }) =>
          isOhelVisit
            ? "N/A"
            : getTransportationCounts(
                studentTransFromCount,
                chapTransFromCount,
                busSize,
              ),
      },
      {
        label: "Transportation From Ohel -> CH Count",
        value: ({
          studentTransReturnCHCount,
          chapTransReturnCHCount,
          busSize,
          isOhelVisit,
        }) =>
          isOhelVisit
            ? getTransportationCounts(
                studentTransReturnCHCount,
                chapTransReturnCHCount,
                busSize,
              )
            : "N/A",
      },
      {
        label: "Transportation From Ohel -> JFK Count",
        value: ({
          studentTransReturnJFKCount,
          chapTransReturnJFKCount,
          busSize,
          isOhelVisit,
        }) =>
          isOhelVisit
            ? getTransportationCounts(
                studentTransReturnJFKCount,
                chapTransReturnJFKCount,
                busSize,
              )
            : "N/A",
      },

      {
        label: "Transportation From Ohel -> LGA Count",
        value: ({
          studentTransReturnLGACount,
          chapTransReturnLGACount,
          busSize,
          isOhelVisit,
        }) =>
          isOhelVisit
            ? getTransportationCounts(
                studentTransReturnLGACount,
                chapTransReturnLGACount,
                busSize,
              )
            : "N/A",
      },
    ];
  };

  return (
    <ExportCSVButton
      className="custom-btn btn btn-accent uppercase-text"
      fileName={`${(programScheduleName || "Trip").replace(
        / /g,
        "_",
      )}_Tour_Schedules`}
      getExportData={() => getTripExport("TourSchedules")}
      getHeaders={getTourSchedulesExportHeaders}
    />
  );
};

interface VenueMetrics<T> {
  venueId: number | null;
  venueName: string;
  metrics: T;
}

interface MetricsApi {
  checkedInAttendees: VenueMetrics<CheckedInAttendees>[];
  event: VenueMetrics<EventMetrics>[];
  studentClasses: VenueMetrics<StudentClassAggregate[]>[];
  tracks: VenueMetrics<TrackAggregate[]>[];
  venuePreferences: VenueMetrics<VenuePreferences>[];
  enrollmentReach: VenueMetrics<EnrollmentReach>[];
  tourSchedules: TourSchedule[];
}

interface EventMetrics {
  allStudentCount: number;
  maleStudentCount: number;
  maleStudentPercentage: number;
  femaleStudentCount: number;
  femaleStudentPercentage: number;
  allChaperonsCount: number;
  chaperonShluchimCount: number;
  chaperonShluchosCount: number;
  chaperoneCount: number;
  otherAttendeesCount: number;
}

interface CheckedInAttendees {
  studentCheckedInCount: number;
  enrollmentAttendeeCheckedInCount: number;
  otherAttendeeCheckedInCount: number;
}

interface StudentClassAggregate {
  classCount: number;
  classDescription: string;
  studentPercentage: number;
}

interface TrackAggregate {
  trackId: number;
  trackName: string;
  trackRegistrationCount: number;
  trackRegistrationPercentage: number;
}

interface VenuePreferences {
  studentCount: number;
  shluchimAndShluchosCount: number;
  chaperoneCount: number;
  totalCount: number;
}

interface EnrollmentReach {
  enrollmentsWithStudentsCount: number;
  campusesWithStudentsCount: number;
  chabadHouseCount: number;
  campusCount: number;
}

interface TourSchedule {
  id: string;
  name: string;
  time: string;
}
